import { useMemo, useState, useEffect } from 'react';
import { App, DatePicker, Form, Select, Space, Switch, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, FilePdfOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DashboardContainer, KPICardsGrid, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from '../style';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { Button } from '@components/ui/Button';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput, AppTextArea } from '@/components/ui/AppInput';
import { useAuthStore } from '@stores/auth.store';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { useStudents } from '@hooks/tenant/useStudents';
import { useAbility } from '@/ability/AbilityProvider';
import {
    FEE_CHARGE_CATEGORIES,
    FEE_CHARGE_FREQUENCIES,
    recordStudentFeePayment,
    subscribeFeeChargeHistory,
    subscribeFeeCharges,
    subscribeStudentFeePayments,
    upsertFeeCharge,
    sendFeeInvoiceNotification,
} from '@services/registry';
import { parseMoneyNumber, formatMoney, toDayjs } from '../finance/shared';
import styled, { createGlobalStyle } from 'styled-components';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text } = Typography;

const FeesTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 14px;
  }

  .ant-tabs-nav::before {
    border-bottom: none !important;
  }

  .ant-tabs-nav-list {
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#232323' : '#f3f4f6')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#303030' : '#e5e7eb')};
    border-radius: 12px;
    padding: 4px;
    gap: 4px;
  }

  .ant-tabs-tab {
    margin: 0 !important;
    border: none !important;
    border-radius: 8px;
    padding: 8px 14px !important;
    background: transparent;
    transition: all 0.2s ease;
  }

  .ant-tabs-tab .ant-tabs-tab-btn {
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.86)' : '#334155')};
    font-weight: 600;
  }

  .ant-tabs-tab:hover .ant-tabs-tab-btn {
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : '#0f172a')};
  }

  .ant-tabs-tab-active {
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#303030' : '#ffffff')} !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : '#0f172a')} !important;
  }

  .ant-tabs-ink-bar {
    display: none;
  }
`;

const FeesModalStyles = createGlobalStyle`
  .fees-charge-modal .ant-modal-content,
  .fees-payment-modal .ant-modal-content,
  .fees-details-modal .ant-modal-content {
    max-height: 92vh;
    display: flex;
    flex-direction: column;
  }

  .fees-charge-modal .ant-modal-header,
  .fees-payment-modal .ant-modal-header,
  .fees-details-modal .ant-modal-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: inherit;
  }

  .fees-charge-modal .ant-modal-footer,
  .fees-payment-modal .ant-modal-footer,
  .fees-details-modal .ant-modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: inherit;
    margin-top: auto;
    padding-top: 14px;
  }

  .fees-charge-modal .ant-modal-body,
  .fees-payment-modal .ant-modal-body,
  .fees-details-modal .ant-modal-body {
    flex: 1;
    overflow: auto;
    max-height: calc(92vh - 160px);
    padding-right: 10px;
    scrollbar-width: thin;
    scrollbar-color: rgba(138, 152, 167, 0.55) transparent;
  }

  .fees-charge-modal .ant-modal-body::-webkit-scrollbar,
  .fees-payment-modal .ant-modal-body::-webkit-scrollbar,
  .fees-details-modal .ant-modal-body::-webkit-scrollbar {
    width: 8px;
  }

  .fees-charge-modal .ant-modal-body::-webkit-scrollbar-track,
  .fees-payment-modal .ant-modal-body::-webkit-scrollbar-track,
  .fees-details-modal .ant-modal-body::-webkit-scrollbar-track {
    background: transparent;
  }

  .fees-charge-modal .ant-modal-body::-webkit-scrollbar-thumb,
  .fees-payment-modal .ant-modal-body::-webkit-scrollbar-thumb,
  .fees-details-modal .ant-modal-body::-webkit-scrollbar-thumb {
    background: rgba(138, 152, 167, 0.45);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .fee-grade-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-right: 8px;
    transition: padding-right 0.16s ease, box-shadow 0.16s ease;
  }

  .fee-grade-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0;
    opacity: 0;
    overflow: hidden;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    padding: 0;
    margin: 0;
    opacity: 0;
    transition: width 0.16s ease, opacity 0.16s ease;
  }

  .fee-grade-chip:hover {
    padding-right: 4px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
  }

  .fee-grade-chip:hover .fee-grade-remove {
    width: 14px;
    opacity: 1;
  }
`;

const categoryOptions = [
    { value: FEE_CHARGE_CATEGORIES.ADMISSION, label: 'Admission Fees' },
    { value: FEE_CHARGE_CATEGORIES.TUITION, label: 'Tuition Fees' },
    { value: FEE_CHARGE_CATEGORIES.OTHER, label: 'Other Charges' },
];

const frequencyOptions = [
    { value: FEE_CHARGE_FREQUENCIES.ONE_TIME, label: 'One time' },
    { value: FEE_CHARGE_FREQUENCIES.MONTHLY, label: 'Monthly' },
    { value: FEE_CHARGE_FREQUENCIES.YEARLY, label: 'Yearly' },
    { value: FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS, label: 'Custom interval (months)' },
];

function getGradeLabel(student) {
    return student?.grade || student?.academicInfo?.currentGrade || student?.className || '—';
}

function getIntervalMonths(charge) {
    if (charge?.frequency === FEE_CHARGE_FREQUENCIES.YEARLY) return 12;
    if (charge?.frequency === FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS) return Math.max(Number(charge?.intervalMonths || 1) || 1, 1);
    return 1;
}

function getImplementationMonth(charge) {
    const start = toDayjs(charge?.implementationDate);
    if (!start || !start.isValid()) return null;
    return start.startOf('month');
}

function getStudentAdmissionMonth(student) {
    const admission = toDayjs(student?.academicInfo?.admissionDate || student?.admissionDate);
    if (!admission || !admission.isValid()) return null;
    return admission.startOf('month');
}

function getChargeStartMonthForStudent(charge, student) {
    const implementationMonth = getImplementationMonth(charge);
    if (!implementationMonth) return null;
    const admissionMonth = getStudentAdmissionMonth(student);
    if (!admissionMonth) return implementationMonth;
    return admissionMonth.isAfter(implementationMonth, 'month') ? admissionMonth : implementationMonth;
}

function countChargeCycles(charge, now, startOverride) {
    const start = startOverride || getImplementationMonth(charge);
    if (!start || !start.isValid()) return 0;
    if (now.isBefore(start, 'month')) return 0;
    if (charge?.frequency === FEE_CHARGE_FREQUENCIES.ONE_TIME) return 1;
    const interval = getIntervalMonths(charge);
    const monthDelta = (now.year() - start.year()) * 12 + (now.month() - start.month());
    return Math.floor(Math.max(monthDelta, 0) / interval) + 1;
}

function getInstallmentPeriodLabel(charge, index, startOverride) {
    const start = startOverride || getImplementationMonth(charge);
    if (!start || !start.isValid()) return '—';
    if (charge?.frequency === FEE_CHARGE_FREQUENCIES.ONE_TIME) return start.format('MMM YYYY');
    const interval = getIntervalMonths(charge);
    return start.add(index * interval, 'month').format('MMM YYYY');
}

function getInstallmentPeriodTs(charge, index, startOverride) {
    const start = startOverride || getImplementationMonth(charge);
    if (!start || !start.isValid()) return 0;
    if (charge?.frequency === FEE_CHARGE_FREQUENCIES.ONE_TIME) return start.startOf('month').valueOf();
    const interval = getIntervalMonths(charge);
    return start.add(index * interval, 'month').startOf('month').valueOf();
}

function periodLabelToTs(label) {
    const d = dayjs(label, 'MMM YYYY', true);
    return d.isValid() ? d.startOf('month').valueOf() : 0;
}

function getNextChargeDate(charge, now) {
    const start = getImplementationMonth(charge);
    if (!start || !start.isValid()) return null;
    if (charge?.frequency === FEE_CHARGE_FREQUENCIES.ONE_TIME) return now.isBefore(start, 'month') ? start : null;
    const interval = getIntervalMonths(charge);
    if (now.isBefore(start, 'month')) return start;
    const cycles = countChargeCycles(charge, now);
    return start.add(cycles * interval, 'month');
}

function getChargeGradesLabel(charge) {
    const normalize = (raw) => {
        if (Array.isArray(raw)) {
            return raw
                .map((g) => {
                    if (!g) return null;
                    if (typeof g === 'string') return g;
                    if (typeof g === 'object') return g.label || g.value || null;
                    return String(g);
                })
                .filter(Boolean);
        }
        if (typeof raw === 'string') {
            return raw.split(',').map((s) => s.trim()).filter(Boolean);
        }
        return [];
    };

    const grades = normalize(charge?.gradeLabels).length
        ? normalize(charge?.gradeLabels)
        : normalize(charge?.gradeList);

    // Backward-compatible display: if gradeList exists, show it even if applyScope is missing/incorrect.
    if (grades.length) return grades.join(', ');
    if (charge?.applyScope === 'specific_grades') return '—';
    return 'All grades';
}

function normalizeGradeValues(raw) {
    if (Array.isArray(raw)) {
        return raw
            .map((g) => {
                if (!g) return null;
                if (typeof g === 'string') return g.trim();
                if (typeof g === 'object') return String(g.value || g.label || '').trim();
                return String(g).trim();
            })
            .filter(Boolean);
    }
    if (typeof raw === 'string') {
        return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
}

function normalizeApplyScope(value, hasGrades = false) {
    const v = String(value || '').trim().toLowerCase();
    if (v === 'specific_grades' || v === 'specific grades' || v === 'specificgrades' || v === 'specific') {
        return 'specific_grades';
    }
    if (hasGrades) return 'specific_grades';
    return 'all_grades';
}

function toIsoDay(value) {
    const d = toDayjs(value);
    return d && d.isValid() ? d.startOf('day').toISOString() : null;
}

export default function FeesManagement() {
    const { message } = App.useApp();
    const ability = useAbility();
    const canManage = !!ability?.can?.('manage', 'FeesManagement');
    const { dateFormat } = useTenantDateFormat();
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId;
    const { rows: students, loading: studentsLoading } = useStudents({ tenantId });

    const [charges, setCharges] = useState([]);
    const [historyRows, setHistoryRows] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loadingCharges, setLoadingCharges] = useState(true);

    const [gradeFilter, setGradeFilter] = useState(undefined);
    const [studentSearch, setStudentSearch] = useState('');
    const [chargeSearch, setChargeSearch] = useState('');

    const [chargeModalOpen, setChargeModalOpen] = useState(false);
    const [editingCharge, setEditingCharge] = useState(null);
    const [chargeForm] = Form.useForm();
    const [savingCharge, setSavingCharge] = useState(false);
    const [gradePreview, setGradePreview] = useState([]);
    const chargeDraft = Form.useWatch([], chargeForm);

    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [paymentForm] = Form.useForm();
    const [savingPayment, setSavingPayment] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsStudent, setDetailsStudent] = useState(null);
    const [historyDetailOpen, setHistoryDetailOpen] = useState(false);
    const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);
    const [sendModalOpen, setSendModalOpen] = useState(false);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [sendTarget, setSendTarget] = useState(null);
    const [sendForm] = Form.useForm();

    const phoneLinkMode = useAuthStore((s) => s.user?.preferences?.phoneLinkMode || 'call');
    const phoneLinkTemplate = useAuthStore((s) => s.user?.preferences?.phoneLinkTemplate || 'https://wa.me/{phone}');
    const phoneCountryCode = useAuthStore((s) => s.user?.preferences?.phoneCountryCode || '92');

    useEffect(() => {
        if (!chargeModalOpen) return;
        // Modal content mounts asynchronously; hydrate form after mount.
        setTimeout(() => {
            chargeForm.resetFields();
            if (editingCharge) {
                const normalizedGrades =
                    normalizeGradeValues(editingCharge?.gradeList).length
                        ? normalizeGradeValues(editingCharge?.gradeList)
                        : normalizeGradeValues(editingCharge?.gradeLabels).length
                            ? normalizeGradeValues(editingCharge?.gradeLabels)
                            : normalizeGradeValues(editingCharge?.grades).length
                                ? normalizeGradeValues(editingCharge?.grades)
                                : normalizeGradeValues(editingCharge?.selectedGrades);
                const resolvedApplyScope = normalizeApplyScope(editingCharge?.applyScope, normalizedGrades.length > 0);

                chargeForm.setFieldsValue({
                    ...editingCharge,
                    frequency: editingCharge?.frequency || FEE_CHARGE_FREQUENCIES.MONTHLY,
                    applyScope: resolvedApplyScope,
                    intervalMonths: Number(editingCharge?.intervalMonths || 1) || 1,
                    gradeList: normalizedGrades,
                    implementationDate: toDayjs(editingCharge?.implementationDate) || dayjs(),
                    amount: formatMoney(editingCharge?.amount || 0),
                    active: editingCharge?.active !== false,
                });
                setGradePreview(normalizedGrades);
                return;
            }
            chargeForm.setFieldsValue({
                category: FEE_CHARGE_CATEGORIES.TUITION,
                frequency: FEE_CHARGE_FREQUENCIES.MONTHLY,
                intervalMonths: 1,
                applyScope: 'all_grades',
                gradeList: [],
                active: true,
                implementationDate: dayjs(),
            });
            setGradePreview([]);
        }, 0);
    }, [chargeModalOpen, editingCharge, chargeForm]);

    useEffect(() => {
        if (!tenantId) {
            setCharges([]);
            setHistoryRows([]);
            setPayments([]);
            setLoadingCharges(false);
            return;
        }

        setLoadingCharges(true);
        const unsubCharges = subscribeFeeCharges({
            tenantId,
            onNext: (rows) => {
                setCharges(rows || []);
                setLoadingCharges(false);
            },
            onError: (err) => {
                console.error(err);
                setLoadingCharges(false);
            },
        });
        const unsubHistory = subscribeFeeChargeHistory({
            tenantId,
            onNext: (rows) => setHistoryRows(rows || []),
            onError: (err) => console.error(err),
        });
        const unsubPayments = subscribeStudentFeePayments({
            tenantId,
            onNext: (rows) => setPayments(rows || []),
            onError: (err) => console.error(err),
        });

        return () => {
            unsubCharges?.();
            unsubHistory?.();
            unsubPayments?.();
        };
    }, [tenantId]);

    const gradeOptions = useMemo(() => {
        const unique = new Set((students || []).map((s) => getGradeLabel(s)).filter((g) => g && g !== '—'));
        return Array.from(unique).sort().map((g) => ({ value: g, label: g }));
    }, [students]);

    const chargeRows = useMemo(() => {
        const q = (chargeSearch || '').trim().toLowerCase();
        if (!q) return charges || [];
        return (charges || []).filter((c) => {
            const hay = `${c.name || ''} ${c.category || ''} ${c.frequency || ''}`.toLowerCase();
            return hay.includes(q);
        });
    }, [charges, chargeSearch]);

    const paymentTotalsByStudentCharge = useMemo(() => {
        const map = new Map();
        for (const p of payments || []) {
            const sid = p.studentId || '';
            const cid = p.chargeId || '';
            const key = `${sid}__${cid}`;
            map.set(key, (map.get(key) || 0) + (Number(p.amount || 0) || 0));
        }
        return map;
    }, [payments]);

    const dueRows = useMemo(() => {
        const now = dayjs();
        const activeCharges = (charges || []).filter((c) => c.active !== false);
        const out = [];

        for (const s of students || []) {
            const grade = getGradeLabel(s);
            if (gradeFilter && grade !== gradeFilter) continue;

            const studentId = s.id || s.authUid;
            if (!studentId) continue;

            let totalExpected = 0;
            let totalPaid = 0;
            const applicableCharges = [];
            const pendingInstallments = [];
            const paidInstallments = [];

            for (const c of activeCharges) {
                const appliesToAll = c.applyScope !== 'specific_grades';
                const allowedGrades = Array.isArray(c.gradeList) ? c.gradeList : [];
                const applies = appliesToAll || allowedGrades.includes(grade);
                if (!applies) continue;

                const studentChargeStartMonth = getChargeStartMonthForStudent(c, s);
                const cycles = countChargeCycles(c, now, studentChargeStartMonth);
                const amountPerCycle = Number(c.amount || 0) || 0;
                const expected = amountPerCycle * cycles;
                const paid = paymentTotalsByStudentCharge.get(`${studentId}__${c.id}`) || 0;
                const pending = Math.max(expected - paid, 0);

                let paidRemaining = paid;
                for (let i = 0; i < cycles; i += 1) {
                    const installmentExpected = amountPerCycle;
                    const installmentPaid = Math.min(Math.max(paidRemaining, 0), installmentExpected);
                    paidRemaining -= installmentPaid;
                    const installmentPending = Math.max(installmentExpected - installmentPaid, 0);
                    if (installmentPending > 0) {
                        pendingInstallments.push({
                            key: `${c.id}_${i}`,
                            chargeId: c.id,
                            name: c.name || 'Charge',
                            periodLabel: getInstallmentPeriodLabel(c, i, studentChargeStartMonth),
                            periodTs: getInstallmentPeriodTs(c, i, studentChargeStartMonth),
                            expected: installmentExpected,
                            paid: installmentPaid,
                            pending: installmentPending,
                        });
                    }
                    if (installmentPaid > 0) {
                        paidInstallments.push({
                            key: `${c.id}_${i}_paid`,
                            chargeId: c.id,
                            name: c.name || 'Charge',
                            periodLabel: getInstallmentPeriodLabel(c, i, studentChargeStartMonth),
                            periodTs: getInstallmentPeriodTs(c, i, studentChargeStartMonth),
                            expected: installmentExpected,
                            paid: installmentPaid,
                            pending: installmentPending,
                        });
                    }
                }

                totalExpected += expected;
                totalPaid += paid;
                applicableCharges.push({
                    chargeId: c.id,
                    name: c.name || 'Charge',
                    expected,
                    paid,
                    pending,
                });
            }

            const pending = Math.max(totalExpected - totalPaid, 0);
            const displayName = s.displayName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Student';
            const searchBlob = `${displayName} ${s.studentId || ''} ${grade}`.toLowerCase();
            if ((studentSearch || '').trim() && !searchBlob.includes(studentSearch.trim().toLowerCase())) continue;

            out.push({
                key: studentId,
                studentId,
                name: displayName,
                grade,
                email: s.email || '',
                phone: s.phone || '',
                parentInfo: s.parentInfo || null,
                fatherPhone: s.fatherPhone || s.parentInfo?.fatherPhone || '',
                motherPhone: s.motherPhone || s.parentInfo?.motherPhone || '',
                guardianPhone: s.guardianPhone || s.parentInfo?.guardianPhone || '',
                fatherEmail: s.fatherEmail || s.parentInfo?.fatherEmail || '',
                motherEmail: s.motherEmail || s.parentInfo?.motherEmail || '',
                guardianEmail: s.guardianEmail || s.parentInfo?.guardianEmail || '',
                emergencyContact: s.emergencyContact || null,
                academicInfo: s.academicInfo || null,
                expected: totalExpected,
                paid: totalPaid,
                pending,
                chargeCount: applicableCharges.length,
                applicableCharges,
                pendingInstallments: pendingInstallments.sort((a, b) => (b.periodTs || 0) - (a.periodTs || 0)),
                paidInstallments: paidInstallments.sort((a, b) => (b.periodTs || 0) - (a.periodTs || 0)),
            });
        }

        return out.sort((a, b) => {
            if (a.grade !== b.grade) return String(a.grade).localeCompare(String(b.grade));
            return String(a.name).localeCompare(String(b.name));
        });
    }, [students, charges, gradeFilter, paymentTotalsByStudentCharge, studentSearch]);

    const kpis = useMemo(() => {
        const totalPending = dueRows.reduce((s, r) => s + (Number(r.pending || 0) || 0), 0);
        const totalPaid = dueRows.reduce((s, r) => s + (Number(r.paid || 0) || 0), 0);
        const activeCharges = (charges || []).filter((c) => c.active !== false).length;
        return { totalPending, totalPaid, activeCharges };
    }, [dueRows, charges]);

    const openCreateCharge = () => {
        setEditingCharge(null);
        setChargeModalOpen(true);
    };

    const openEditCharge = (row) => {
        setEditingCharge(row);
        setChargeModalOpen(true);
    };

    const onSaveCharge = async () => {
        if (!tenantId) return;
        if (!canManage) {
            message.error('You have view-only access for Fees Management.');
            return;
        }
        try {
            const v = await chargeForm.validateFields();
            const amount = parseMoneyNumber(v.amount);
            if (!amount || amount <= 0) {
                chargeForm.setFields([{ name: 'amount', errors: ['Amount must be greater than 0'] }]);
                return;
            }
            const normalizedApplyScope = normalizeApplyScope(v.applyScope, (v.gradeList || []).length > 0 || gradePreview.length > 0);
            const normalizedGradeList = normalizedApplyScope === 'specific_grades'
                ? (normalizeGradeValues(v.gradeList).length ? normalizeGradeValues(v.gradeList) : normalizeGradeValues(gradePreview))
                : [];

            const nextPayload = {
                name: (v.name || '').trim(),
                category: v.category,
                amount,
                frequency: v.frequency,
                intervalMonths: v.frequency === FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS ? Number(v.intervalMonths || 1) || 1 : 1,
                applyScope: normalizedApplyScope,
                gradeList: normalizedGradeList,
                gradeLabels: normalizedGradeList,
                implementationDate: v.implementationDate?.toDate?.() || v.implementationDate || new Date(),
                notes: v.notes || '',
                active: Boolean(v.active),
            };

            let payloadToSend = nextPayload;
            if (editingCharge?.id) {
                const prevApplyScope = normalizeApplyScope(editingCharge?.applyScope, normalizeGradeValues(editingCharge?.gradeList || editingCharge?.gradeLabels).length > 0);
                const prevGradeList = normalizeGradeValues(editingCharge?.gradeList || editingCharge?.gradeLabels);
                const prevDateIso = toIsoDay(editingCharge?.implementationDate);
                const nextDateIso = toIsoDay(nextPayload.implementationDate);
                const eqArray = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

                const patch = {};
                if ((editingCharge?.name || '') !== nextPayload.name) patch.name = nextPayload.name;
                if ((editingCharge?.category || '') !== nextPayload.category) patch.category = nextPayload.category;
                if ((Number(editingCharge?.amount || 0) || 0) !== nextPayload.amount) patch.amount = nextPayload.amount;
                if ((editingCharge?.frequency || '') !== nextPayload.frequency) patch.frequency = nextPayload.frequency;
                if ((Number(editingCharge?.intervalMonths || 1) || 1) !== nextPayload.intervalMonths) patch.intervalMonths = nextPayload.intervalMonths;
                if (prevApplyScope !== nextPayload.applyScope) patch.applyScope = nextPayload.applyScope;
                if (!eqArray(prevGradeList, nextPayload.gradeList)) {
                    patch.gradeList = nextPayload.gradeList;
                    patch.gradeLabels = nextPayload.gradeLabels;
                }
                if (prevDateIso !== nextDateIso) patch.implementationDate = nextPayload.implementationDate;
                if ((editingCharge?.notes || '') !== nextPayload.notes) patch.notes = nextPayload.notes;
                if (Boolean(editingCharge?.active) !== nextPayload.active) patch.active = nextPayload.active;

                payloadToSend = patch;
            }
            setSavingCharge(true);
            await upsertFeeCharge({
                tenantId,
                chargeId: editingCharge?.id,
                actor: { name: user?.name, email: user?.email },
                payload: payloadToSend,
            });
            message.success(editingCharge?.id ? 'Charge updated' : 'Charge created');
            setChargeModalOpen(false);
            setEditingCharge(null);
            setGradePreview([]);
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e?.message || 'Failed to save charge');
        } finally {
            setSavingCharge(false);
        }
    };

    const canSaveCharge = useMemo(() => {
        if (!canManage || savingCharge) return false;
        if (!chargeModalOpen) return false;
        if (!editingCharge?.id) return true;
        const v = chargeDraft || {};
        const draftApplyScope = normalizeApplyScope(v.applyScope, normalizeGradeValues(v.gradeList).length > 0 || gradePreview.length > 0);
        const prevApplyScope = normalizeApplyScope(editingCharge?.applyScope, normalizeGradeValues(editingCharge?.gradeList || editingCharge?.gradeLabels).length > 0);
        const draftGradeList = draftApplyScope === 'specific_grades'
            ? (normalizeGradeValues(v.gradeList).length ? normalizeGradeValues(v.gradeList) : normalizeGradeValues(gradePreview))
            : [];
        const prevGradeList = prevApplyScope === 'specific_grades' ? normalizeGradeValues(editingCharge?.gradeList || editingCharge?.gradeLabels) : [];
        const eqArray = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
        const draftAmount = parseMoneyNumber(v.amount);
        const prevAmount = Number(editingCharge?.amount || 0) || 0;
        const draftInterval = v.frequency === FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS ? Number(v.intervalMonths || 1) || 1 : 1;
        const prevInterval = Number(editingCharge?.intervalMonths || 1) || 1;
        const sameDate = toIsoDay(v.implementationDate) === toIsoDay(editingCharge?.implementationDate);

        return !(
            (String(v.name || '').trim() === String(editingCharge?.name || '').trim()) &&
            ((v.category || '') === (editingCharge?.category || '')) &&
            (draftAmount === prevAmount) &&
            ((v.frequency || '') === (editingCharge?.frequency || '')) &&
            (draftInterval === prevInterval) &&
            (draftApplyScope === prevApplyScope) &&
            eqArray(draftGradeList, prevGradeList) &&
            sameDate &&
            (String(v.notes || '') === String(editingCharge?.notes || '')) &&
            (Boolean(v.active) === Boolean(editingCharge?.active))
        );
    }, [canManage, savingCharge, chargeModalOpen, editingCharge, chargeDraft, gradePreview]);

    const openRecordPayment = (row, chargeRow) => {
        setSelectedStudent(row);
        paymentForm.resetFields();
        paymentForm.setFieldsValue({
            chargeId: chargeRow?.chargeId || undefined,
            amount: chargeRow?.pending ? formatMoney(chargeRow.pending) : '',
            paymentDate: dayjs(),
        });
        setPaymentModalOpen(true);
    };

    const paymentChargeOptions = useMemo(() => {
        const list = selectedStudent?.applicableCharges || [];
        return list
            .filter((c) => Number(c.pending || 0) > 0)
            .map((c) => ({
                value: c.chargeId,
                label: `${c.name} (Pending: ${formatMoney(c.pending)})`,
            }));
    }, [selectedStudent]);

    const onRecordPayment = async () => {
        if (!tenantId || !selectedStudent?.studentId) return;
        if (!canManage) {
            message.error('You have view-only access for Fees Management.');
            return;
        }
        try {
            const v = await paymentForm.validateFields();
            const amount = parseMoneyNumber(v.amount);
            if (!amount || amount <= 0) {
                paymentForm.setFields([{ name: 'amount', errors: ['Amount must be greater than 0'] }]);
                return;
            }
            setSavingPayment(true);
            await recordStudentFeePayment({
                tenantId,
                payment: {
                    studentId: selectedStudent.studentId,
                    chargeId: v.chargeId,
                    amount,
                    paidAt: v.paymentDate?.toDate?.() || v.paymentDate || new Date(),
                    note: (v.note || '').trim(),
                    createdBy: {
                        uid: user?.uid || null,
                        name: user?.name || null,
                        email: user?.email || null,
                    },
                },
            });
            message.success('Payment recorded');
            setPaymentModalOpen(false);
            setSelectedStudent(null);
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e?.message || 'Failed to record payment');
        } finally {
            setSavingPayment(false);
        }
    };

    const openDetails = (row) => {
        setDetailsStudent(row);
        setDetailsOpen(true);
    };

    const openHistoryDetail = (row) => {
        setSelectedHistoryRow(row);
        setHistoryDetailOpen(true);
    };

    const toCurrency = (n) => `${formatMoney(Number(n || 0) || 0)}`;

    const loadImageAsDataUrl = async (url) => {
        if (!url) return null;
        try {
            const res = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            return await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    };

    const buildInvoiceDoc = async ({ studentRow, invoiceTitle, lineItems }) => {
        const doc = new jsPDF();
        const schoolName = user?.schoolName || 'School';
        const invoiceDate = dayjs().format(dateFormat);
        const logoData = await loadImageAsDataUrl(user?.logoUrl);

        if (logoData) {
            try {
                doc.addImage(logoData, 'PNG', 14, 12, 22, 22);
            } catch {
                // ignore bad logo format
            }
        }

        doc.setFillColor(14, 165, 233);
        doc.rect(0, 0, 210, 36, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.text(schoolName, 40, 18);
        doc.setFontSize(11);
        doc.text(invoiceTitle || 'Fees Invoice', 40, 25);
        doc.setFontSize(10);
        doc.text(`Date: ${invoiceDate}`, 150, 20);
        doc.setTextColor(15, 23, 42);

        doc.setFontSize(11);
        doc.text(`Student: ${studentRow.name || '—'}`, 14, 45);
        doc.text(`Grade: ${studentRow.grade || '—'}`, 14, 52);
        const totalPaid = (lineItems || []).reduce((s, i) => s + (Number(i.paid || 0) || 0), 0);
        const totalPending = (lineItems || []).reduce((s, i) => s + (Number(i.pending || 0) || 0), 0);
        doc.text(`Total Paid: ${toCurrency(totalPaid)}`, 14, 59);
        doc.text(`Total Pending: ${toCurrency(totalPending)}`, 14, 66);

        const chargeBody = (lineItems || []).map((c) => [
            c.name || 'Charge',
            toCurrency(c.expected),
            toCurrency(c.paid),
            toCurrency(c.pending),
        ]);

        autoTable(doc, {
            startY: 74,
            head: [['Charge', 'Expected', 'Paid', 'Pending']],
            body: chargeBody.length ? chargeBody : [['No charges', '-', '-', '-']],
            styles: { fontSize: 10 },
            headStyles: { fillColor: [14, 165, 233] },
        });

        const safeName = (studentRow.name || 'student').replace(/\s+/g, '_');
        const fileName = `${safeName}_${(invoiceTitle || 'invoice').replace(/\s+/g, '_').toLowerCase()}_${dayjs().format('YYYYMMDD')}.pdf`;
        return { doc, fileName };
    };

    const buildInvoicePdf = async ({ studentRow, invoiceTitle, lineItems }) => {
        try {
            const { doc, fileName } = await buildInvoiceDoc({ studentRow, invoiceTitle, lineItems });
            doc.save(fileName);
        } catch (e) {
            message.error(e?.message || 'Failed to generate invoice PDF');
        }
    };

    const studentEmail = (student) => {
        const fromParent = student?.parentInfo?.fatherEmail || student?.parentInfo?.guardianEmail;
        return String(student?.email || student?.guardianEmail || student?.fatherEmail || student?.motherEmail || fromParent || '').trim();
    };
    const studentPhone = (student) => {
        // Requirement: use father phone (if available) for WhatsApp delivery.
        return String(student?.fatherPhone || student?.parentInfo?.fatherPhone || '').trim();
    };

    const normalizeForWhatsApp = (rawPhone) => {
        let digits = String(rawPhone || '').replace(/\D/g, '');
        if (!digits) return '';
        if (digits.startsWith('00')) digits = digits.slice(2);
        if (digits.startsWith('0')) return `${phoneCountryCode}${digits.slice(1)}`;
        if (digits.length === 10) return `${phoneCountryCode}${digits}`;
        return digits;
    };

    const buildWhatsAppHref = (rawPhone, text) => {
        const template = phoneLinkTemplate || 'https://wa.me/{phone}';
        const phoneDigits = normalizeForWhatsApp(rawPhone);
        let url = template.includes('{phone}') ? template.replace('{phone}', phoneDigits) : template;
        const hasQuery = url.includes('?');
        const hasTextParam = /[?&]text=/.test(url);
        if (!hasTextParam) {
            url = `${url}${hasQuery ? '&' : '?'}text=${encodeURIComponent(text)}`;
        }
        return url;
    };

    const blobToBase64 = async (blob) => {
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const out = String(reader.result || '');
                const b64 = out.includes(',') ? out.split(',')[1] : out;
                resolve(b64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const openSendInvoice = (studentRow, chargeRow) => {
        setSendTarget({ studentRow, chargeRow });
        sendForm.setFieldsValue({ channel: phoneLinkMode === 'whatsapp' ? undefined : 'email' });
        setSendModalOpen(true);
    };

    const onSendInvoice = async () => {
        if (!tenantId || !sendTarget?.studentRow || !sendTarget?.chargeRow) return;
        try {
            const v = await sendForm.validateFields();
            const channel = v?.channel;
            const studentRow = sendTarget.studentRow;
            const chargeRow = sendTarget.chargeRow;

            const email = studentEmail(studentRow);
            const phone = studentPhone(studentRow);
            if (channel === 'email' && !email) {
                message.error('Student email not found.');
                return;
            }
            if (channel === 'whatsapp' && !phone) {
                message.error('Student phone not found for WhatsApp.');
                return;
            }

            setSendingInvoice(true);
            const invoiceTitle = 'Pending Charge Invoice';
            const { doc, fileName } = await buildInvoiceDoc({
                studentRow,
                invoiceTitle,
                lineItems: [chargeRow],
            });
            const blob = doc.output('blob');
            const pdfBase64 = await blobToBase64(blob);

            const chargeName = chargeRow?.name || 'Fee Charge';
            const amountDue = toCurrency(chargeRow?.pending || 0);
            const period = chargeRow?.periodLabel || 'Current period';
            const studentName = studentRow?.name || 'Student';
            const schoolName = user?.schoolName || 'School';
            const noteText = `Dear Parent/Guardian,\n\nThis is a reminder from ${schoolName} regarding pending school fees for ${studentName}.\nCharge: ${chargeName}\nPeriod: ${period}\nPending Amount: ${amountDue}\n\nPlease complete the payment at your earliest convenience. If payment has already been made, kindly ignore this message.\n\nThank you,\n${schoolName} Accounts Office`;

            try {
                await sendFeeInvoiceNotification({
                    tenantId,
                    payload: {
                        channel,
                        recipient: channel === 'email' ? email : phone,
                        subject: `Fee Invoice - ${studentName} - ${period}`,
                        message: noteText,
                        fileName,
                        pdfBase64,
                        meta: {
                            studentId: studentRow?.studentId,
                            chargeId: chargeRow?.chargeId,
                            period,
                        },
                    },
                });
                if (channel === 'email') {
                    message.success('Invoice sent via email.');
                } else {
                    message.success('WhatsApp message requested. Attachment depends on backend implementation.');
                }
            } catch (sendErr) {
                // Fallback: browser cannot attach a PDF to WhatsApp/email automatically.
                // So we open composer with text only (no auto-download).
                if (sendErr?.message === 'SEND_INVOICE_FUNCTION_NOT_CONFIGURED') {
                    if (channel === 'email') {
                        const subject = encodeURIComponent(`Fee Invoice - ${studentName} - ${period}`);
                        const body = encodeURIComponent(`${noteText}\n\n(Invoice PDF could not be attached automatically. Configure backend send function to attach.)`);
                        window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank', 'noopener,noreferrer');
                        message.info('Email composer opened (text only).');
                    } else {
                        const waUrl = buildWhatsAppHref(phone, noteText);
                        window.open(waUrl, '_blank', 'noopener,noreferrer');
                        message.info('WhatsApp opened (text only). Configure backend send function to attach PDF.');
                    }
                    console.warn(sendErr);
                    setSendModalOpen(false);
                    setSendTarget(null);
                    sendForm.resetFields();
                    return;
                }

                console.error(sendErr);
                message.error(sendErr?.message || 'Failed to send invoice');
                return;
            }

            setSendModalOpen(false);
            setSendTarget(null);
            sendForm.resetFields();
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e?.message || 'Failed to send invoice');
        } finally {
            setSendingInvoice(false);
        }
    };

    const downloadSummaryInvoicePdf = async (row) => {
        await buildInvoicePdf({
            studentRow: row,
            invoiceTitle: 'Fees Summary Invoice',
            lineItems: row?.applicableCharges || [],
        });
    };

    const downloadSingleChargeInvoicePdf = async (studentRow, chargeRow, mode = 'pending') => {
        const title = mode === 'paid' ? 'Paid Charge Invoice' : 'Pending Charge Invoice';
        await buildInvoicePdf({
            studentRow,
            invoiceTitle: title,
            lineItems: [chargeRow],
        });
    };

    const chargeColumns = [
        { title: 'Charge', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
        {
            title: 'Category',
            key: 'category',
            render: (_, row) => <Tag color={row.category === 'tuition' ? 'blue' : row.category === 'admission' ? 'purple' : 'gold'}>{row.category || 'other'}</Tag>,
        },
        { title: 'Amount', key: 'amount', render: (_, row) => <Text strong>{formatMoney(row.amount || 0)}</Text> },
        {
            title: 'Frequency',
            key: 'frequency',
            render: (_, row) => {
                if (row.frequency === FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS) return `Every ${row.intervalMonths || 1} months`;
                if (row.frequency === FEE_CHARGE_FREQUENCIES.MONTHLY) return 'Monthly';
                if (row.frequency === FEE_CHARGE_FREQUENCIES.YEARLY) return 'Yearly';
                return 'One time';
            },
        },
        {
            title: 'Implementation date',
            key: 'implementationDate',
            render: (_, row) => {
                const d = toDayjs(row.implementationDate);
                return d ? d.format(dateFormat) : '—';
            },
        },
        {
            title: 'Grades',
            key: 'grades',
            render: (_, row) => getChargeGradesLabel(row),
        },
        {
            title: 'Next run',
            key: 'nextRun',
            render: (_, row) => {
                const next = getNextChargeDate(row, dayjs());
                return next ? next.format(dateFormat) : 'Completed';
            },
        },
        {
            title: 'Active',
            key: 'active',
            render: (_, row) => <Tag color={row.active !== false ? 'green' : 'default'}>{row.active !== false ? 'Active' : 'Inactive'}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => (
                <Space>
                    <Button type="button" $primary={false} onClick={() => openEditCharge(row)} disabled={!canManage}>Edit</Button>
                </Space>
            ),
        },
    ];

    const studentDueColumns = [
        { title: 'Student', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
        { title: 'Grade', dataIndex: 'grade', key: 'grade', render: (v) => v || '—' },
        { title: 'Applied Charges', key: 'chargeCount', render: (_, row) => row.chargeCount || 0 },
        { title: 'Paid Amount', key: 'paid', render: (_, row) => <Text style={{ color: '#16a34a' }}>{formatMoney(row.paid || 0)}</Text> },
        { title: 'Pending Amount', key: 'pending', render: (_, row) => <Text strong style={{ color: row.pending > 0 ? '#ef4444' : undefined }}>{formatMoney(row.pending || 0)}</Text> },
    ];

    const historyColumns = [
        {
            title: 'Date',
            key: 'date',
            render: (_, row) => {
                const d = toDayjs(row.createdAt) || toDayjs(row.createdAtLocal);
                return d ? d.format(`${dateFormat} HH:mm`) : '—';
            },
        },
        { title: 'Action', dataIndex: 'action', key: 'action', render: (v) => <Tag>{v || 'updated'}</Tag> },
        { title: 'Charge', key: 'charge', render: (_, row) => row?.after?.name || row?.before?.name || '—' },
        {
            title: 'Amount',
            key: 'amount',
            render: (_, row) => {
                const amount = Number(row?.after?.amount ?? row?.before?.amount ?? 0) || 0;
                return <Text strong>{formatMoney(amount)}</Text>;
            },
        },
        { title: 'Implementation date', key: 'impl', render: (_, row) => {
            const d = toDayjs(row?.after?.implementationDate || row?.before?.implementationDate);
            return d ? d.format(dateFormat) : '—';
        } },
        {
            title: 'Grades',
            key: 'grades',
            render: (_, row) => getChargeGradesLabel(row?.after || row?.before || {}),
        },
        {
            title: 'Frequency',
            key: 'frequency',
            render: (_, row) => {
                const f = row?.after?.frequency || row?.before?.frequency;
                if (f === FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS) {
                    return `Every ${row?.after?.intervalMonths || row?.before?.intervalMonths || 1} months`;
                }
                if (f === FEE_CHARGE_FREQUENCIES.MONTHLY) return 'Monthly';
                if (f === FEE_CHARGE_FREQUENCIES.YEARLY) return 'Yearly';
                if (f === FEE_CHARGE_FREQUENCIES.ONE_TIME) return 'One time';
                return '—';
            },
        },
    ];

    const tabItems = [
        {
            key: 'charges',
            label: 'Fees Charges',
            children: (
                <Table
                    title="Fee Charges"
                    columns={chargeColumns}
                    dataSource={chargeRows}
                    loading={loadingCharges}
                    emptyText="No fee charges configured yet"
                    searchValue={chargeSearch}
                    onSearchChange={setChargeSearch}
                    pagination
                    pageSize={8}
                    showSearch
                    showPagination
                    showFilter={false}
                    showMore={false}
                />
            ),
        },
        {
            key: 'dues',
            label: 'Dues Ledger',
            children: (
                <div>
                    <StudentsHeaderRow style={{ marginBottom: 10 }}>
                        <StudentsHeaderLeft>
                            <StudentsHeaderTitle style={{ fontSize: 18 }}>Student Dues</StudentsHeaderTitle>
                            <StudentsHeaderSub>Sort and review pending/paid fee amounts by grade.</StudentsHeaderSub>
                        </StudentsHeaderLeft>
                        <Space size={10} wrap>
                            <Select
                                allowClear
                                value={gradeFilter}
                                onChange={(v) => setGradeFilter(v || undefined)}
                                placeholder="Filter grade"
                                style={{ minWidth: 180 }}
                                options={gradeOptions}
                            />
                        </Space>
                    </StudentsHeaderRow>
                    <Table
                        title="Dues Ledger"
                        columns={studentDueColumns}
                        dataSource={dueRows}
                        loading={studentsLoading}
                        emptyText="No student due records"
                        searchValue={studentSearch}
                        onSearchChange={setStudentSearch}
                        pagination
                        pageSize={10}
                        showSearch
                        showPagination
                        showFilter={false}
                        showMore={false}
                        onRowClick={openDetails}
                    />
                </div>
            ),
        },
        {
            key: 'history',
            label: 'History',
            children: (
                <Table
                    title="Charge Change History"
                    columns={historyColumns}
                    dataSource={historyRows}
                    loading={loadingCharges}
                    emptyText="No change history yet"
                    showSearch={false}
                    pagination
                    pageSize={8}
                    showPagination
                    showFilter={false}
                    showMore={false}
                    onRowClick={openHistoryDetail}
                />
            ),
        },
    ];

    return (
        <DashboardContainer>
            <FeesModalStyles />
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Fees Management</StudentsHeaderTitle>
                    <StudentsHeaderSub>Configure fee charges, implementation schedule, and monitor pending/paid amounts by grade.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <Space size={10} wrap>
                    <Tooltip title={!canManage ? 'Locked: view-only access for Fees Management.' : undefined}>
                        <span>
                            <Button type="primary" onClick={openCreateCharge} disabled={!canManage}>Add Fee Charge</Button>
                        </span>
                    </Tooltip>
                </Space>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={3} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Active Charges" value={kpis.activeCharges} />
                <KPICard title="Total Pending Amount" value={formatMoney(kpis.totalPending)} />
                <KPICard title="Total Paid Amount" value={formatMoney(kpis.totalPaid)} />
            </KPICardsGrid>

            <FeesTabs defaultActiveKey="charges" items={tabItems} />

            <AppModal
                title={editingCharge?.id ? 'Edit fee charge' : 'Create fee charge'}
                open={chargeModalOpen}
                className="fees-charge-modal"
                onCancel={() => {
                    setChargeModalOpen(false);
                    setEditingCharge(null);
                    setGradePreview([]);
                }}
                onOk={() => void onSaveCharge()}
                okText={editingCharge?.id ? 'Save changes' : 'Save charge'}
                size="md"
                destroyOnClose
                okButtonProps={{
                    disabled: !canSaveCharge,
                    title: !canManage
                        ? 'Locked: view-only access for Fees Management.'
                        : (editingCharge?.id ? 'No changes to save yet.' : undefined),
                }}
                confirmLoading={savingCharge}
            >
                <Form form={chargeForm} layout="vertical" preserve={false}>
                    <Form.Item name="name" label="Charge name" rules={[{ required: true, message: 'Enter charge name' }]}>
                        <AppInput placeholder="e.g. Tuition Fee - Grade 10" />
                    </Form.Item>
                    <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Select category' }]}>
                        <Select options={categoryOptions} />
                    </Form.Item>
                    <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Enter amount' }]}>
                        <AppInput isPrice placeholder="0" />
                    </Form.Item>
                    <Form.Item name="frequency" label="Charge frequency" rules={[{ required: true, message: 'Select frequency' }]}>
                        <Select options={frequencyOptions} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.frequency !== next.frequency}>
                        {({ getFieldValue }) =>
                            getFieldValue('frequency') === FEE_CHARGE_FREQUENCIES.CUSTOM_MONTHS ? (
                                <Form.Item
                                    name="intervalMonths"
                                    label="Interval (months)"
                                    rules={[{ required: true, message: 'Enter interval months' }]}
                                >
                                    <AppInput placeholder="e.g. 6" />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                    <Form.Item name="applyScope" label="Apply to" rules={[{ required: true, message: 'Select apply scope' }]}>
                        <Select
                            options={[
                                { value: 'all_grades', label: 'All grades' },
                                { value: 'specific_grades', label: 'Specific grades' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) => prev.applyScope !== next.applyScope}
                    >
                        {({ getFieldValue }) => {
                            const specific = normalizeApplyScope(getFieldValue('applyScope')) === 'specific_grades';
                            const selectedSet = new Set(gradePreview);
                            const remainingGradeOptions = (gradeOptions || []).filter((o) => {
                                const v = String(o?.value ?? o?.label ?? '').trim();
                                return v && !selectedSet.has(v);
                            });
                            return (
                                <>
                                    <Form.Item
                                        name="gradeList"
                                        label="Grades"
                                        rules={[
                                            {
                                                validator: async (_, value) => {
                                                    if (!specific) return;
                                                const selected = normalizeGradeValues(value).length
                                                    ? normalizeGradeValues(value)
                                                    : normalizeGradeValues(gradePreview);
                                                if (selected.length > 0) return;
                                                    throw new Error('Select grades');
                                                },
                                            },
                                        ]}
                                    >
                                        <Select
                                            mode="multiple"
                                            options={remainingGradeOptions}
                                            placeholder={specific ? 'Select grades' : 'All grades selected automatically'}
                                            optionFilterProp="label"
                                            disabled={!specific}
                                            style={{ width: '100%' }}
                                            tagRender={() => null}
                                            onChange={(vals) => setGradePreview(normalizeGradeValues(vals))}
                                        />
                                    </Form.Item>
                                    {specific && gradePreview.length ? (
                                        <div style={{ marginTop: -6, marginBottom: 12 }}>
                                            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                                                Selected grades
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {gradePreview.map((g) => (
                                                    <Tag
                                                        key={g}
                                                        className="fee-grade-chip"
                                                        color="blue"
                                                    >
                                                        {g}
                                                        <button
                                                            type="button"
                                                            className="fee-grade-remove"
                                                            aria-label={`Remove ${g}`}
                                                            onClick={() => {
                                                                const next = gradePreview.filter((x) => x !== g);
                                                                setGradePreview(next);
                                                                chargeForm.setFieldsValue({ gradeList: next });
                                                            }}
                                                        >
                                                            <DeleteOutlined style={{ fontSize: 11 }} />
                                                        </button>
                                                    </Tag>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </>
                            );
                        }}
                    </Form.Item>
                    <Form.Item
                        name="implementationDate"
                        label="Implementation date"
                        rules={[{ required: true, message: 'Select implementation date' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format={dateFormat} />
                    </Form.Item>
                    <Form.Item name="active" label="Active" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <AppTextArea rows={3} placeholder="Optional notes for this charge" />
                    </Form.Item>
                </Form>
            </AppModal>

            <AppModal
                title={`Fee details${detailsStudent?.name ? ` - ${detailsStudent.name}` : ''}`}
                open={detailsOpen}
                className="fees-details-modal"
                onCancel={() => {
                    setDetailsOpen(false);
                    setDetailsStudent(null);
                }}
                onOk={() => setDetailsOpen(false)}
                okText="Close"
                width="92vw"
                style={{ maxWidth: 1200 }}
                destroyOnClose
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <Text type="secondary">
                        Student: <Text strong>{detailsStudent?.name || '—'}</Text> | Grade: <Text strong>{detailsStudent?.grade || '—'}</Text>
                    </Text>
                    <Tooltip title={!detailsStudent ? 'No student selected.' : undefined}>
                        <span>
                            <Button type="primary" onClick={() => void downloadSummaryInvoicePdf(detailsStudent)} icon={<FilePdfOutlined />} disabled={!detailsStudent}>
                                Download Summary PDF
                            </Button>
                        </span>
                    </Tooltip>
                </div>
                <FeesTabs
                    defaultActiveKey="pending"
                    items={[
                        {
                            key: 'pending',
                            label: 'Pending',
                            children: (
                                <Table
                                    title="Pending Charges"
                                    columns={[
                                        { title: 'Charge', dataIndex: 'name', key: 'name' },
                                        { title: 'Period', dataIndex: 'periodLabel', key: 'periodLabel', render: (v) => v || '—' },
                                        { title: 'Pending', key: 'pending', render: (_, r) => <Text strong style={{ color: '#ef4444' }}>{toCurrency(r.pending)}</Text> },
                                        {
                                            title: 'Actions',
                                            key: 'act',
                                            render: (_, r) => (
                                                <Space>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        onClick={() => openRecordPayment(detailsStudent, r)}
                                                        disabled={!canManage || r.pending <= 0}
                                                        title={!canManage ? 'Locked: view-only access.' : (r.pending <= 0 ? 'No pending amount for this period.' : '')}
                                                    >
                                                        Pay
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        icon={<FilePdfOutlined />}
                                                        onClick={() => void downloadSingleChargeInvoicePdf(detailsStudent, r, 'pending')}
                                                    >
                                                        PDF
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        icon={<SendOutlined />}
                                                        onClick={() => openSendInvoice(detailsStudent, r)}
                                                        disabled={!canManage}
                                                        title={!canManage ? 'Locked: view-only access.' : ''}
                                                    >
                                                        Send
                                                    </Button>
                                                </Space>
                                            ),
                                        },
                                    ]}
                                    dataSource={[...(detailsStudent?.pendingInstallments || [])].sort(
                                        (a, b) => ((b.periodTs ?? periodLabelToTs(b.periodLabel)) || 0) - ((a.periodTs ?? periodLabelToTs(a.periodLabel)) || 0)
                                    )}
                                    emptyText="No pending charges"
                                    showSearch={false}
                                    showFilter={false}
                                    showMore={false}
                                    pagination={false}
                                    showPagination={false}
                                />
                            ),
                        },
                        {
                            key: 'paid',
                            label: 'Paid',
                            children: (
                                <Table
                                    title="Paid Charges"
                                    columns={[
                                        { title: 'Charge', dataIndex: 'name', key: 'name' },
                                        { title: 'Expected', key: 'expected', render: (_, r) => toCurrency(r.expected) },
                                        { title: 'Period', dataIndex: 'periodLabel', key: 'periodLabel', render: (v) => v || '—' },
                                        { title: 'Paid', key: 'paid', render: (_, r) => <Text style={{ color: '#16a34a' }}>{toCurrency(r.paid)}</Text> },
                                        { title: 'Pending', key: 'pending', render: (_, r) => toCurrency(r.pending) },
                                        {
                                            title: 'Actions',
                                            key: 'act',
                                            render: (_, r) => (
                                                <Space>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        onClick={() => openRecordPayment(detailsStudent, r)}
                                                        disabled={!canManage || r.pending <= 0}
                                                        title={!canManage ? 'Locked: view-only access.' : (r.pending <= 0 ? 'No pending amount for this charge.' : '')}
                                                    >
                                                        Pay
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        icon={<FilePdfOutlined />}
                                                        onClick={() => void downloadSingleChargeInvoicePdf(detailsStudent, r, 'paid')}
                                                    >
                                                        PDF
                                                    </Button>
                                                </Space>
                                            ),
                                        },
                                    ]}
                                    dataSource={[...(detailsStudent?.paidInstallments || [])].sort(
                                        (a, b) => ((b.periodTs ?? periodLabelToTs(b.periodLabel)) || 0) - ((a.periodTs ?? periodLabelToTs(a.periodLabel)) || 0)
                                    )}
                                    emptyText="No paid charges"
                                    showSearch={false}
                                    showFilter={false}
                                    showMore={false}
                                    pagination={false}
                                    showPagination={false}
                                />
                            ),
                        },
                    ]}
                />
            </AppModal>

            <AppModal
                title={`Record payment${selectedStudent?.name ? ` - ${selectedStudent.name}` : ''}`}
                open={paymentModalOpen}
                className="fees-payment-modal"
                onCancel={() => {
                    setPaymentModalOpen(false);
                    setSelectedStudent(null);
                }}
                onOk={() => void onRecordPayment()}
                okText="Save payment"
                size="md"
                destroyOnClose
                okButtonProps={{
                    disabled: savingPayment || !canManage,
                    title: !canManage ? 'Locked: view-only access for Fees Management.' : undefined,
                }}
                confirmLoading={savingPayment}
            >
                <Form form={paymentForm} layout="vertical" preserve={false}>
                    <Form.Item name="chargeId" label="Charge" rules={[{ required: true, message: 'Select charge' }]}>
                        <Select options={paymentChargeOptions} placeholder="Select pending charge" />
                    </Form.Item>
                    <Form.Item name="amount" label="Amount paid" rules={[{ required: true, message: 'Enter paid amount' }]}>
                        <AppInput isPrice placeholder="0" />
                    </Form.Item>
                    <Form.Item name="paymentDate" label="Payment date" rules={[{ required: true, message: 'Select payment date' }]}>
                        <DatePicker style={{ width: '100%' }} format={dateFormat} />
                    </Form.Item>
                    <Form.Item name="note" label="Note">
                        <AppInput placeholder="Optional receipt or reference note" />
                    </Form.Item>
                </Form>
            </AppModal>

            <AppModal
                title={`Send invoice${sendTarget?.studentRow?.name ? ` - ${sendTarget.studentRow.name}` : ''}`}
                open={sendModalOpen}
                className="fees-payment-modal"
                onCancel={() => {
                    setSendModalOpen(false);
                    setSendTarget(null);
                    sendForm.resetFields();
                }}
                onOk={() => void onSendInvoice()}
                okText="Send invoice"
                size="md"
                destroyOnClose
                confirmLoading={sendingInvoice}
                okButtonProps={{ disabled: !canManage || sendingInvoice }}
            >
                <Form form={sendForm} layout="vertical" preserve={false}>
                    <Form.Item
                        name="channel"
                        label="Send via"
                        rules={[{ required: true, message: 'Select send channel' }]}
                    >
                        <Select
                            placeholder="Select channel"
                            options={[
                                { value: 'email', label: 'Email' },
                                ...(phoneLinkMode === 'whatsapp' ? [{ value: 'whatsapp', label: 'WhatsApp' }] : []),
                            ]}
                        />
                    </Form.Item>
                    <Form.Item label="Student Email">
                        <AppInput value={studentEmail(sendTarget?.studentRow)} disabled placeholder="No email found" />
                    </Form.Item>
                    <Form.Item label="Father Phone">
                        <AppInput value={studentPhone(sendTarget?.studentRow)} disabled placeholder="No father phone found" />
                    </Form.Item>
                    <Text type="secondary">
                        A professional fee reminder message and invoice PDF will be sent to the selected channel.
                    </Text>
                </Form>
            </AppModal>

            <AppModal
                title="History detail"
                open={historyDetailOpen}
                onCancel={() => {
                    setHistoryDetailOpen(false);
                    setSelectedHistoryRow(null);
                }}
                onOk={() => {
                    setHistoryDetailOpen(false);
                    setSelectedHistoryRow(null);
                }}
                okText="Close"
                size="lg"
                destroyOnClose
            >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>Action</div>
                        <div><Tag>{selectedHistoryRow?.action || 'updated'}</Tag></div>
                    </div>
                    <div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>Date</div>
                        <div>{(toDayjs(selectedHistoryRow?.createdAt) || toDayjs(selectedHistoryRow?.createdAtLocal))?.format(`${dateFormat} HH:mm`) || '—'}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>Created by</div>
                        <div>{selectedHistoryRow?.action === 'created' ? (selectedHistoryRow?.actorName || selectedHistoryRow?.actorEmail || '—') : '—'}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>Updated by</div>
                        <div>{selectedHistoryRow?.action !== 'created' ? (selectedHistoryRow?.actorName || selectedHistoryRow?.actorEmail || '—') : '—'}</div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ border: '1px solid rgba(15,23,42,0.12)', borderRadius: 10, padding: 10 }}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Before</Text>
                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                            <div><b>Name:</b> {selectedHistoryRow?.before?.name || '—'}</div>
                            <div><b>Category:</b> {selectedHistoryRow?.before?.category || '—'}</div>
                            <div><b>Amount:</b> {formatMoney(selectedHistoryRow?.before?.amount || 0)}</div>
                            <div><b>Frequency:</b> {selectedHistoryRow?.before?.frequency || '—'}</div>
                            <div><b>Grades:</b> {getChargeGradesLabel(selectedHistoryRow?.before || {})}</div>
                            <div><b>Implementation:</b> {(toDayjs(selectedHistoryRow?.before?.implementationDate))?.format(dateFormat) || '—'}</div>
                            <div><b>Notes:</b> {selectedHistoryRow?.before?.notes || '—'}</div>
                        </div>
                    </div>
                    <div style={{ border: '1px solid rgba(15,23,42,0.12)', borderRadius: 10, padding: 10 }}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>After</Text>
                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                            <div><b>Name:</b> {selectedHistoryRow?.after?.name || '—'}</div>
                            <div><b>Category:</b> {selectedHistoryRow?.after?.category || '—'}</div>
                            <div><b>Amount:</b> {formatMoney(selectedHistoryRow?.after?.amount || 0)}</div>
                            <div><b>Frequency:</b> {selectedHistoryRow?.after?.frequency || '—'}</div>
                            <div><b>Grades:</b> {getChargeGradesLabel(selectedHistoryRow?.after || {})}</div>
                            <div><b>Implementation:</b> {(toDayjs(selectedHistoryRow?.after?.implementationDate))?.format(dateFormat) || '—'}</div>
                            <div><b>Notes:</b> {selectedHistoryRow?.after?.notes || '—'}</div>
                        </div>
                    </div>
                </div>
            </AppModal>
        </DashboardContainer>
    );
}
